import type { Course, Enrollment, Requirement } from '@/types/domain';

export interface ReqStatus {
  met: boolean;
  pending?: boolean;
  detail?: string | null;
}

/** Compute per-requirement completion state for a course + enrollment. */
export function requirementState(c: Course, e?: Enrollment): Record<Requirement, ReqStatus> {
  const m = {} as Record<Requirement, ReqStatus>;
  for (const r of c.requirements) {
    if (r === 'pretest') m[r] = { met: !!e && e.pretest != null, detail: e?.pretest != null ? `${e.pretest}%` : null };
    if (r === 'lessons')
      m[r] = {
        met: !!e && e.progress === 100,
        detail: e ? `${Math.round((e.progress / 100) * c.lessons.length)}/${c.lessons.length}` : `0/${c.lessons.length}`,
      };
    if (r === 'attend') m[r] = { met: e?.attended === true, pending: e?.attended === null, detail: c.seminar ?? null };
    if (r === 'posttest')
      m[r] = {
        met: !!e && e.posttest != null && e.posttest >= c.passScore,
        pending: !!e && e.posttest == null && e.pretest != null,
        detail: `≥ ${c.passScore}%`,
      };
    if (r === 'eval') m[r] = { met: e?.eval === true };
    if (r === 'approval') m[r] = { met: false, pending: true };
  }
  return m;
}

/** How many requirements are satisfied. */
export function metCount(c: Course, e?: Enrollment): number {
  const rs = requirementState(c, e);
  return c.requirements.filter((k) => rs[k]?.met).length;
}

/**
 * Certificate issuance mode:
 *  - 'manual' when the course's requirements include `approval`
 *    (an instructor must approve & issue),
 *  - 'auto'   otherwise (issues automatically once all requirements are met).
 */
export function issuanceMode(c: Course): 'auto' | 'manual' {
  return c.requirements.includes('approval') ? 'manual' : 'auto';
}

/** True when every certificate requirement (other than manual approval) is met. */
export function eligibleForCert(c: Course, e?: Enrollment): boolean {
  if (!e) return false;
  const rs = requirementState(c, e);
  return c.requirements.filter((r) => r !== 'approval').every((r) => rs[r]?.met);
}

export interface CourseGating {
  enrolled: boolean;
  capacityFull: boolean;
  enrollmentClosed: boolean;
  /** can a non-enrolled learner enroll right now? */
  canEnroll: boolean;
  /** pre-test must be taken before lessons unlock */
  preTestRequired: boolean;
  preTestDone: boolean;
  lessonsLocked: boolean;
  /** check-in stays locked until every lesson is complete */
  checkinLocked: boolean;
  /** post-test stays locked until the learner has checked in (attended) */
  postTestLocked: boolean;
  evalRequired: boolean;
  evalDone: boolean;
}

/**
 * Derives the enroll/lock/gate states a course-detail screen needs.
 * `readonly`/`hidden` courses and full classes block enrollment; a required
 * pre-test (enrollment has a `pretest` slot) gates the lessons until taken.
 */
export function courseGating(c: Course, e?: Enrollment): CourseGating {
  const enrolled = !!e;
  const capacityFull = c.enrolled >= c.capacity;
  const enrollmentClosed = c.status === 'readonly' || c.status === 'hidden';
  const preTestRequired = c.requirements.includes('posttest') && c.lessons.length > 0;
  const preTestDone = !!e && e.pretest != null;
  const evalRequired = c.requirements.includes('eval');
  const evalDone = e?.eval === true;
  return {
    enrolled,
    capacityFull,
    enrollmentClosed,
    canEnroll: !enrolled && !capacityFull && !enrollmentClosed,
    preTestRequired,
    preTestDone,
    lessonsLocked: enrolled && preTestRequired && !preTestDone,
    checkinLocked: enrolled && c.requirements.includes('attend') && c.lessons.some((l) => !l.done),
    postTestLocked: enrolled && c.requirements.includes('posttest') && e?.attended !== true,
    evalRequired,
    evalDone,
  };
}
